// Components for displaying sets of stories.
import { StyleSheet, View } from "react-native";
import { FlowBox, Pad, PadBox, ShadowBox } from "component/basics";
import { FilterButton, ActionButton } from "component/button";
import { Datastore, TestState } from "util/datastore";
import { global_textinput_test_handlers, Heading, UtilityText } from "component/text";
import { useRef, useState } from "react";
import { camelCaseToTitleCase, kebabCaseToTitleCase, keysToTrueMap, pauseAsync } from "util/util";
import { Reset } from "@carbon/icons-react";
import { colorBorderPrimary, colorSurfaceSecondary, colorTextSecondary } from "component/color";
import { Banner } from "component/banner";
import { closeActivePopup, getPopupRef } from "platform-specific/popup.web";
import { assembleConfig } from "util/features";
import { getStructureForKey, StructureDemo } from "util/instance";
import { personaA } from "util/testpersonas";
import { DemoFile, DemoSection, DemoStorySetType } from "./demo";
import React from "react";

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function DemoStorySet({storySet = null as DemoStorySetType | null, language}) {
    const domRef = useRef<HTMLDivElement>(null);
    const dataRef = useRef<Datastore>(null);
    const [key, setKey] = useState(0);
    const [navInstance, setNavInstance] = useState<NavInstance | null>(null);
    const [callLog, setCallLog] = useState([] as Record<string, any>[]);

    if (!storySet) return null;

    const { content, siloKey='demo', structureKey='testStruct', instanceKey='testInstance',
        config, features, pad=true, ...testState
    } = storySet;
    function onServerCall(call) : Promise<any> {
        setCallLog(oldLog => [...oldLog, call]);
        return Promise.resolve();
    }

    let featureConfig = null;
    if (features) {
        if (!storySet.structureKey) {
            throw new Error('Structure key required for feature config');
        }
        const structure = getStructureForKey(structureKey);
        if (!structure) {
            throw new Error('Structure not found: ' + structureKey);
        }
        featureConfig = assembleConfig({
            structure, activeFeatures: keysToTrueMap(features), 
            includeDefaults: false
        });
    }

    function onReset() {
        dataRef.current?.resetData();
        dataRef.current?.resetSessionData();
        setNavInstance(null);
        setKey(key+1);
        setCallLog([]);
    }

    const extendedTestState : TestState = {
        ...testState,
        language,
        gotoInstance: setNavInstance,
        onServerCall,
        goBack: () => setNavInstance({parent: true}),
        openUrl: url => setNavInstance({url}),
        closeWindow: () => setNavInstance({close: true}),
        pushSubscreen: (screenKey, params) => setNavInstance({screenKey, params}),
        serverCall: {...defaultServerCall, ...storySet.serverCall},
    }

    return <Datastore ref={dataRef} config={featureConfig ?? config} 
        siloKey={siloKey} structureKey={structureKey} instanceKey={instanceKey}
        testState={extendedTestState} isLive={false} language={language}
        personaPreview={storySet.personaPreview ?? personaA}
    >
        <Heading text={storySet.label} />
        <Pad size={5} />
        {storySet.stories && storySet.stories.length > 0 && <FlowBox>
            {storySet.stories?.map(story =>
                <PadBox right={10} vert={5} key={story.label}>
                    <FilterButton text={story.label} 
                        onPress={() => playStoryActionListAsync({domRef, actions: story.actions})} />
                </PadBox>
            )}
            <PadBox vert={5}><ActionButton icon={Reset} text='Reset' onPress={onReset} /></PadBox>
        </FlowBox>
        }
        <Pad size={10} />
        <ShadowBox>
            <PadBox horiz={pad ? 20 : 0} vert={pad ? 20 : 0}>
                {navInstance && <NavResult navInstance={navInstance} /> }
                <View key={key}>
                    <div ref={domRef}>
                        {content}
                    </div>
                </View>
                {callLog.length > 0 && <ServerCallLog callLog={callLog} />}
            </PadBox>
        </ShadowBox>
        <Pad size={32} />
    </Datastore>    
}

export function NavResult({navInstance}) {
    return <PadBox horiz={8} vert={8}>
        <Banner>
            <UtilityText text='Navigated to:' />
            {navInstance.close && <UtilityText weight='medium' text='Close window' />}
            {navInstance.url && <UtilityText weight='medium' text={navInstance.url} />}
            {navInstance.parent && <UtilityText weight='medium' text='Parent screen' />}
            {navInstance.structureKey && <UtilityText weight='medium' text={navInstance.structureKey + '/' + navInstance.instanceKey} />}
            {navInstance.screenKey && <UtilityText weight='medium' text={'Subscreen: ' + navInstance.screenKey + '(' + JSON.stringify(navInstance.params) + ')'} />}
            {navInstance.token && <UtilityText weight='medium' text={'Login token: ' + navInstance.token} />}
            {navInstance.params && Object.keys(navInstance.params).length > 0 && <UtilityText weight='medium' text={'Params: ' + JSON.stringify(navInstance.params)} />}
        </Banner>
    </PadBox>
}

export function ServerCallLog({callLog}) {
    const s = ServerCallLogStyle;
    return <View style={s.callList}>
        {callLog.map((call, i) => 
            <UtilityText type='tiny' weight='medium' color={colorTextSecondary} key={i} text={call.component + '.' + call.funcname + '(' + JSON.stringify(call.params) +')'} />
        )}
    </View>
}

type NavInstance = {
    close?: boolean;
    url?: string;
    parent?: boolean;
    structureKey?: string;
    instanceKey?: string;
    screenKey?: string;
    params?: Record<string, any>;
}


const ServerCallLogStyle = StyleSheet.create({
    callList: {
        backgroundColor: colorSurfaceSecondary,
        padding: 6,
        marginTop: 16,
        borderColor: colorBorderPrimary,
        borderWidth: 1,
    }
})

async function playStoryActionListAsync({domRef, actions}) {
    for (const action of actions) {
        await playStoryAction({domRef, action});
        await pauseAsync(500);
    }
    await pauseAsync(500);
}

export const defaultServerCall = {
    eventlog: {
        logEvent: () => {return Promise.resolve()}
    }
}


async function playStoryAction({domRef, action}) {
    console.log('playStoryAction', action, domRef);

    if (action.action === 'popup') {
        const popup = document.body.querySelector('[data-testid="popup-content"]');
        return await playStoryAction({domRef: {current: popup}, action: action.popupAction});
    } else if (action.action === 'popup-close') {
        return closeActivePopup();
    }
    
    if (action.action === 'check_text') {
        const textFound = domRef.current?.textContent.includes(action.expectedText);
        if (!textFound) {
            throw new Error(`Expected text "${action.expectedText}" not found.`);
        }
        return;
    }

    if (action.action === 'key_press') {
        const event = new KeyboardEvent('keydown', action.key);
        window.dispatchEvent(event);
        return;
    }

    const node = findStoryActionNode({domRef, matcher: action.matcher});
    if (!node) {
        throw new Error('Node not found: ' + action.matcher);
    } else if (action.action === 'click') {
        node.click();
    } else if (action.action === 'hover') {
        const hasPointer = typeof window !== 'undefined' && 'PointerEvent' in window;
        const eventType = hasPointer ? 'pointerenter' : 'mouseenter';
        const EventClass = hasPointer ? PointerEvent : MouseEvent;
        const event = new EventClass(eventType, {bubbles: true});
        node.dispatchEvent(event);
    } else if (action.action === 'within') {
        return playStoryAction({domRef: {current: node}, action: action.innerAction});
    } else if (action.action === 'input') {
        const onChangeText = global_textinput_test_handlers[action.matcher];
        onChangeText(action.text);
    } else if (action.action == 'popup') {
        await playStoryAction({domRef: getPopupRef(), action: action.popupAction});
    } else {
        throw new Error('Unsupported action: ' + action.action);
    }
}

function findStoryActionNode({domRef, matcher}) {
    if (typeof matcher === 'string') {
        return domRef.current.querySelector('[data-testid="' + matcher + '"]');
    } else {
        throw new Error('Unsupported matcher type: ' + matcher);
    }
}
// TODO: Cope with arbitrary paths with a nice tree browser


export function pathToSectionNameAndLabel(path: string) {
    const parts = path.split('/');
    if (parts[0] == 'open-modules') {
        if (parts[2] == 'feature' && parts.length == 5) {
            return {
                sectionName: kebabCaseToTitleCase(parts[3]),
                label: kebabCaseToTitleCase(parts[4])
            }
        } else if (parts[2] == 'Structure') {  
            return {
                sectionName: 'Structure',
                label: kebabCaseToTitleCase(parts[parts.length - 1])
            }
        } else {
            return {
                sectionName: parts[1].toUpperCase() + ' Components',
                label: kebabCaseToTitleCase(parts[parts.length - 1])
            }
        }
            
    }
    if (parts[0] == 'feature') {
        return {
            sectionName: kebabCaseToTitleCase(parts[1]),
            label: kebabCaseToTitleCase(parts[parts.length - 1])
        };
    } else if (parts[0] == 'component') {
        return {
            // sectionName: 'Core Design System',
            sectionName: 'Core Platform Components',
            label: kebabCaseToTitleCase(parts[1])
        };
    } else if (parts[0] == 'structure') {
        return {
            sectionName: 'Structure',
            label: kebabCaseToTitleCase(parts[1])
        };
    } else if (parts[0] == 'contrib') {
        return {
            sectionName: kebabCaseToTitleCase(parts[1]),
            label: kebabCaseToTitleCase(parts[parts.length - 1])
        };
    } else {
        throw new Error('Unsupported demo path: ' + path);
    }
}

export function demoFilesToComponentDemoSections(demoFiles: DemoFile[]): DemoSection[] {
    const sections: DemoSection[] = [];

    for (const demoFile of demoFiles) {
        if (demoFile.componentDemos.length == 0) continue;

        const { sectionName, label } = pathToSectionNameAndLabel(demoFile.path);
        let section = sections.find(s => s.label == sectionName);
        if (!section) {
            section = { label: sectionName, key: 'component-' + sectionName.toLowerCase().replace(/ /g, '-'), pages: [] };
            sections.push(section);
        }

        let page = section.pages.find(p => p.label == label);
        if (!page) {
            page = {
                label, key: demoFile.path.replaceAll('/', '-'),
                designUrl: demoFile.designUrl, storySetList: []
            };
            section.pages.push(page);
        }

        for (const componentDemo of demoFile.componentDemos) {
            for (const variant of componentDemo.variants) {
                if (!page.storySetList) {
                    page.storySetList = [];
                }
                const combinedProps = { ...componentDemo.props, ...variant.props };
                const combinedArgs = { ...componentDemo.props?.args, ...variant.props?.args };
                page.storySetList.push({
                    ...componentDemo.props.state,
                    ...variant.props.state,
                    structureKey: combinedProps.structureKey,
                    siloKey: combinedProps.siloKey,
                    instanceKey: combinedProps.instanceKey,
                    label: camelCaseToTitleCase(componentDemo.name) + (variant.name ? (' : ' + variant.name) : ''),
                    key: componentDemo.name + '-' + variant.name,
                    content: <componentDemo.component {...combinedArgs} />,
                    config: combinedProps.config,
                    features: combinedProps.features,
                    stories: variant.stories,
                });
            }
        }
    }
    return sections;
}

export function FeatureDemoScreen({ featureDemo }) {
    return <StructureDemo structureKey={featureDemo.structureKey}
        instanceKey={featureDemo.instanceKey}
        features={featureDemo.features}
        testState={featureDemo.state}
        personaPreview={featureDemo.personaPreview}
        language={featureDemo.language}
        roles={featureDemo.roles} />;
}

export function demoFilesToFeatureDemoSections(demoFiles: DemoFile[]): DemoSection[] {
    const sections: DemoSection[] = [];

    for (const demoFile of demoFiles) {
        const { sectionName, label } = pathToSectionNameAndLabel(demoFile.path);

        if (demoFile.featureDemo) {
            let section = sections.find(s => s.label == sectionName);
            if (!section) {
                section = { label: sectionName, key: 'feature-' + sectionName.toLowerCase().replace(/ /g, '-'), pages: [] };
                sections.push(section);
            }

            const featureDemo = demoFile.featureDemo;
            const page = {
                label, key: label.toLowerCase().replace(/ /g, '-'),
                designUrl: demoFile.designUrl,
                screen: () => <FeatureDemoScreen featureDemo={featureDemo} />
            };
            section.pages.push(page);
        }
    }

    return sections;
}

